import type { AnyOrama, Nullable, Orama, Results, SearchParams } from '@orama/orama'
import { load, search as oramaSearch, save } from '@orama/orama'
import type { ClientSearchParams, SearchMode } from '@oramacloud/client'
import { OramaClient } from '@oramacloud/client'
import { batch, effect, signal } from '@preact/signals-core'
import debounce from 'lodash.debounce'
import type { RegisterSearchBoxParams } from '../SearchBox'
import {
  COLOR_SCHEME_SYSTEM,
  DEFAULT_RETURNABLE_PROPS,
  NO_RESULTS_TEXT_DEFAULT,
  RESULTS_NUMBER,
  SEARCH_INPUT_PLACEHOLDER_DEFAULT,
  SEARCH_RESULTS_PLACEHOLDER_DEFAULT,
  SHOW_FOOTER_KEYBOARD_SHORTCUTS,
  SHOW_POWERED_BY
} from '../const'
import { searchboxConfig as defaultConfig } from '../defaultConfig'
import { INVALID_INSTANCE, ORAMA_CLIENT_NOT_INITIALIZED_YET, SEARCH_WENT_WRONG } from '../errors'
import type {
  ColorScheme,
  DocsDocument,
  Mode,
  ReturningProps,
  SearchResults,
  SetSeeAllLinkProps,
  SummaryChatMessage,
  Theme
} from '../types'
import { type SearchResultFacets, groupSearchResultsBySection } from '../utils'

export const backdrop = signal<boolean>(false)
export const searchableProperties = signal<ReturningProps>(DEFAULT_RETURNABLE_PROPS)
export const show = signal<boolean>(false)
export const keepOpen = signal<boolean>(false)
export const onClose = signal<(() => void) | undefined>(undefined)
export const keyboardNavigationHints = signal<boolean>(true)
export const showShowMoreLink = signal<boolean>(false)
export const mode = signal<Nullable<Mode>>(null)
export const searchTerm = signal<Nullable<string>>('')
export const queryTerm = signal<Nullable<string>>(null)
export const searchResults = signal<Nullable<SearchResults>>(null)
export const searchResultsListing = signal<Nullable<SearchResults>>(null)
export const oramaClient = signal<Nullable<OramaClient | AnyOrama>>(null)
export const showFooterKeyboardShortcuts = signal<boolean>(SHOW_FOOTER_KEYBOARD_SHORTCUTS)
export const showPoweredBy = signal<boolean>(SHOW_POWERED_BY)
export const searchInputPlaceholder = signal<string>(SEARCH_INPUT_PLACEHOLDER_DEFAULT)
export const searchResultsPlaceholder = signal<string>(SEARCH_RESULTS_PLACEHOLDER_DEFAULT)
export const noResultsTemplate = signal<string>(NO_RESULTS_TEXT_DEFAULT)
export const colorScheme = signal<ColorScheme>(COLOR_SCHEME_SYSTEM)
export const showFullscreenOnMobile = signal<boolean>(true)
export const showResultIcon = signal<boolean>(false)
export const ready = signal<boolean>(false)
export const searchMode = signal<SearchMode>('fulltext')
export const facetProperty = signal<string>('')
export const facets = signal<SearchResultFacets>({})
export const facetsListing = signal<SearchResultFacets>({})
export const focusedResultIndex = signal<number>(-1)
export const themeConfig = signal<typeof defaultConfig.ui.theme>(defaultConfig.ui.theme)
export const isSummaryClamped = signal<boolean>(false)
export const isSummaryExpanded = signal<boolean>(false)
export const currentFacet = signal<string>('all')
export const searchPagination = signal(0)
export const limitMultiplier = signal(1)
export const summaryHistory = signal<SummaryChatMessage[]>([])
export const setResultTitle = signal<Nullable<(doc: DocsDocument) => string>>(null)
export const theme = signal<Theme>('primary')
export const loadingAnswer = signal<boolean>(false)
const searchedTermsMap: string[] = []

export const seeAllLink = signal<{
  url: string
  label: string | ((count: string, term?: string) => string)
}>({
  url: '/search',
  label: 'See all results'
})
export const searchParams = signal<SearchParams<Orama<AnyOrama | OramaClient>>>({})
export const renderItem = signal<Nullable<(doc: DocsDocument) => React.ReactElement>>(null)

export const setResultLink = signal<Nullable<(doc: DocsDocument) => string>>(null)

export async function searchWithOrama(params: SearchParams<AnyOrama>) {
  if (oramaClient.value && typeof (oramaClient.value as OramaClient)?.search === 'function') {
    const oramaCloudClient = oramaClient.value as OramaClient
    return oramaCloudClient.search(params as ClientSearchParams)
  }

  return oramaSearch(oramaClient.value as AnyOrama, params)
}

export async function search(term: Nullable<string>, limitMultiplier = 1, searchByFacet = false) {
  if (!term) {
    return null
  }

  const { limit, offset, where, ...restSearchParams } = searchParams.value
  const currentLimit = limit ?? RESULTS_NUMBER

  const params = {
    ...restSearchParams,
    term,
    limit: currentLimit * limitMultiplier,
    offset: offset ? offset * currentLimit : searchPagination.value * currentLimit,
    ...(where ? { where } : {}),
    ...(facetProperty.value
      ? {
          facets: {
            [facetProperty.value]: {}
          },
          ...(searchByFacet && currentFacet.value && currentFacet.value !== 'all'
            ? {
                where: {
                  [facetProperty.value]: {
                    eq: currentFacet.value
                  },
                  ...where
                }
              }
            : {})
        }
      : {})
  }

  let results: Nullable<Results<DocsDocument>> = null

  if (oramaClient.value && typeof (oramaClient.value as OramaClient)?.search === 'function') {
    try {
      const oramaCloudClient = oramaClient.value as OramaClient
      results = await oramaCloudClient.search(params as ClientSearchParams)
    } catch (e) {
      // @todo Add link to docs when done
      throw new Error(SEARCH_WENT_WRONG)
    }
  } else {
    results = await oramaSearch(oramaClient.value as AnyOrama, params)
  }

  // debouncedSummary({ term })

  const facets = results?.facets
    ? {
        all: Object.keys(results?.facets[facetProperty.value].values).reduce(
          (acc, key) => acc + (results?.facets ? results?.facets[facetProperty.value].values[key] : 0),
          0
        ),
        ...results?.facets[facetProperty.value].values
      }
    : {
        all: results?.count
      }

  const groupedResults = groupSearchResultsBySection(
    (results as Results<DocsDocument>) ?? { hits: [] },
    searchableProperties.value
  )

  return {
    results: {
      ...results,
      groupedResults
    },
    facets
  }
}

// biome-ignore lint/suspicious/noExplicitAny: Will remove this any as soon as we publish the correct types
let answerSession: any = null

function createAnswerSession() {
  if (!oramaClient.value) {
    throw new Error(ORAMA_CLIENT_NOT_INITIALIZED_YET)
  }
  return (oramaClient.value as OramaClient).createAnswerSession({
    events: {
      onMessageChange: debounce((messages: SummaryChatMessage[]) => {
        loadingAnswer.value = false
        summaryHistory.value = messages
      }, 15),
      onMessageLoading: (loading: boolean) => {
        loadingAnswer.value = loading
      }
    }
  })
}

export async function getSummary({ term }: { term: string }) {
  if (!answerSession) {
    if (!oramaClient.value) {
      throw new Error(ORAMA_CLIENT_NOT_INITIALIZED_YET)
    }

    answerSession = createAnswerSession()
  }

  // if the last question was the same ignore it, we just changed facets
  if (searchedTermsMap.reverse()[0] !== term) {
    searchedTermsMap.push(term)
    answerSession.ask({
      limit: 5,
      ...searchParams.value,
      term
    })
  }
}

export function abortAnswer() {
  if (answerSession) {
    answerSession.abortAnswer()
  }
}

effect(async () => {
  const term = searchTerm.value
  const multiplier = limitMultiplier.value

  if (!term) {
    searchResults.value = null
    return
  }

  const res = await search(term, multiplier, true)

  searchResults.value = res?.results as SearchResults
  facets.value = res?.facets as SearchResultFacets
  facetsListing.value = facetsListing.value =
    Object.keys(facetsListing.value).length > 0 ? facetsListing.value : facets.value

  if (!Object.keys(facetsListing.value).includes(currentFacet.value)) {
    currentFacet.value = 'all'
  }

  setSeeAllLink({
    label: undefined,
    term,
    count: res?.results?.count?.toString()
  })
})

effect(async () => {
  const querySearchTerm = queryTerm.value
  const multiplier = limitMultiplier.value

  if (!querySearchTerm) {
    searchResultsListing.value = null
    return
  }

  const queryResults = await search(querySearchTerm, multiplier, true)
  searchResultsListing.value = queryResults?.results as SearchResults
  facetsListing.value = queryResults?.facets as SearchResultFacets
})

export async function setOramaClientFromCloudConfig(cloudConfig: RegisterSearchBoxParams['cloudConfig']) {
  if (!cloudConfig?.key || !cloudConfig.url) {
    throw new Error(INVALID_INSTANCE)
  }
  oramaClient.value = new OramaClient({
    api_key: cloudConfig.key,
    endpoint: cloudConfig.url
  })
}

export async function setOramaClient(data: AnyOrama | OramaClient) {
  if (typeof (data as OramaClient)?.search === 'function') {
    oramaClient.value = data
    return
  }

  const dataAsAnyOrama = data as AnyOrama
  // check if it's actually AnyOrama
  if (dataAsAnyOrama?.data?.docs) {
    const rawData = await save(dataAsAnyOrama)
    await load(dataAsAnyOrama, rawData)

    oramaClient.value = dataAsAnyOrama
    return
  }
  throw new Error(INVALID_INSTANCE)
}

export function setSearchResultsMap(data: Partial<ReturningProps>) {
  if (data.path) {
    searchableProperties.value.path = data.path
  }
  if (data.title) {
    searchableProperties.value.title = data.title
  }
  if (data.description) {
    searchableProperties.value.description = data.description
  }

  if (data.section) {
    searchableProperties.value.section = data.section
  }
  if (data.category) {
    searchableProperties.value.category = data.category
  }
}

export function setSeeAllLink({ label, term, count }: Partial<SetSeeAllLinkProps>) {
  if (!term) return seeAllLink.value

  if (seeAllLink.value.url.includes('?q=') || seeAllLink.value.url.includes('&q=')) {
    seeAllLink.value.url = seeAllLink.value.url.replace(/q=[^&]+/, `q=${term}`)
  } else {
    seeAllLink.value.url = `${seeAllLink.value.url}${seeAllLink.value.url.includes('?') ? '&' : '?'}q=${term}`
  }

  if (!count) return

  if (typeof label === 'function') {
    seeAllLink.value.label = label(count, term)
  }
}

export async function generateFollowUpSummary(msg: SummaryChatMessage) {
  if (!answerSession) {
    answerSession = createAnswerSession()
  }

  answerSession.ask({
    ...searchParams.value,
    term: msg.content,
    limit: 5
  })
}

export function closeAndReset() {
  show.value && onClose?.value?.()
  batch(() => {
    show.value = false
    searchTerm.value = ''
    searchResults.value = null
    searchResultsListing.value = null
    queryTerm.value = null
    searchPagination.value = 0
    focusedResultIndex.value = -1
    currentFacet.value = 'all'
    limitMultiplier.value = 1
    isSummaryClamped.value = true
    isSummaryExpanded.value = false
    summaryHistory.value = []
  })

  answerSession = null
}
